import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useDeleteGoalByGoalId,
  useGetGoalsListByProgramId,
} from "../../../../../../../../api/services/program/programApis";
import {
  MuiBox,
  StatusBadgeType,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import { ButtonWithIcon } from "../../../../../../../../components/buttonWithIcon/ButtonWithIcon";
import {
  getValueOrBlank,
  isStatusClosed,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../../../../../redux/slices/program/programSelector";
import { ParticipantDetail } from "../../../../../../modelsAndSchemas/Models";
import { GoalSelectionWrapper } from "./GoalSelection.style";
import { goalSelectionSiteCoreKeys } from "./SiteCoreKeys";
import { AddGoal } from "./components/addGoal/AddGoal";
import { GoalSelectionResult } from "./components/goalSelectionResult/GoalSelectionResult";
import { ViewGoal } from "./components/viewGoal/ViewGoal";

interface GoalSelectionProps {
  pageId?: string;
  programId: string;
  isViewOnly?: boolean;
  participantDetail?: ParticipantDetail;
}
export const GoalSelection: FC<GoalSelectionProps> = ({
  programId,
  isViewOnly = false,
  participantDetail,
}) => {
  const { useFetchLabel, commonLabels } = useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showViewGoalModal, setShowViewGoalModal] = useState(false);
  const [showDeleteGoalConfirmation, setShowDeleteGoalConfirmation] =
    useState(false);
  const goalIdRef = useRef<string | undefined>();
  const isEditRef = useRef<boolean>(false);
  const programStatusId = useSelector(programStatusSelector);
  const isProgramStatusClosed = useMemo(
    () => isStatusClosed(programStatusId as StatusBadgeType),
    [programStatusId],
  );
  const {
    goalSelectionTitle,
    addGoalButton,
    deleteGoalConfirmationMessage,
    goalDeletedMessage,
  } = goalSelectionSiteCoreKeys;
  const getLabel = useFetchLabel(goalSelectionTitle);

  const {
    mutateAsync: fetchGoalsListByProgramId,
    isPending: isGoalsListPending,
    data: goalsListData,
    isError: isGoalsListError,
  } = useGetGoalsListByProgramId();

  const {
    mutateAsync: deleteGoalByGoalId,
    isPending: isDeleteGoalPending,
    isError: isDeleteGoalError,
    isSuccess: isDeleteGoalSuccess,
  } = useDeleteGoalByGoalId();

  useEffect(() => {
    // Fetch goals list when component mounts
    fetchGoalsListByProgramId(programId);
  }, [programId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isFetchingGoals: {
          isVisible: isGoalsListPending || isDeleteGoalPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      toggleLoader({
        isFetchingGoals: {
          isVisible: false,
        },
      });
    };
  }, [isGoalsListPending, isDeleteGoalPending]);

  useEffect(() => {
    if (isGoalsListError || isDeleteGoalError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
      isDeleteGoalError && handleDeleteGoalConfirmationModalClose();
    }
  }, [isGoalsListError, isDeleteGoalError]);

  useEffect(() => {
    if (isDeleteGoalSuccess) {
      displayToast(getLabel(goalDeletedMessage));
      handleDeleteGoalConfirmationModalClose();
      fetchGoalsListByProgramId(programId);
    }
  }, [isDeleteGoalSuccess]);

  const handleAddGoalClick = () => {
    setShowAddGoalModal(true);
  };

  const handleGoalAdded = (programId: string) => {
    fetchGoalsListByProgramId(programId);
  };

  const handleAddGoalModalClose = () => {
    setShowAddGoalModal(false);
    isEditRef.current = false;
    clearGoalIdRef();
  };

  const setGoalIdRef = (goalId: string) => {
    goalIdRef.current = goalId;
  };

  const clearGoalIdRef = () => {
    goalIdRef.current = undefined;
  };

  const handleEditGoal = (goalId: string) => {
    setGoalIdRef(goalId);
    isEditRef.current = true;
    setShowAddGoalModal(true);
  };

  const handleViewGoal = (goalId: string) => {
    setGoalIdRef(goalId);
    setShowViewGoalModal(true);
  };

  const handleViewModalClose = () => {
    clearGoalIdRef();
    setShowViewGoalModal(false);
  };

  const handleDeleteGoalConfirmation = (goalId: string) => {
    setGoalIdRef(goalId);
    setShowDeleteGoalConfirmation(true);
  };

  const handleDeleteGoalConfirmationModalClose = () => {
    clearGoalIdRef();
    setShowDeleteGoalConfirmation(false);
  };

  const handleGoalDelete = () => {
    deleteGoalByGoalId(getValueOrBlank(goalIdRef.current));
  };

  return (
    <GoalSelectionWrapper>
      {!isViewOnly && (
        <MuiBox className="semi-header">
          <ButtonWithIcon
            buttonText={getLabel(addGoalButton)}
            onActionClick={handleAddGoalClick}
            disabled={isProgramStatusClosed}
          />
        </MuiBox>
      )}

      <GoalSelectionResult
        rows={goalsListData ?? []}
        onViewClick={handleViewGoal}
        onDeleteClick={handleDeleteGoalConfirmation}
        onEditClick={handleEditGoal}
        isViewOnly={isViewOnly}
      />

      {showAddGoalModal && (
        <AddGoal
          open={showAddGoalModal}
          onClose={handleAddGoalModalClose}
          programId={programId}
          onGoalAdded={handleGoalAdded}
          participantDetail={participantDetail}
          isEdit={isEditRef.current}
          goalId={getValueOrBlank(goalIdRef.current)}
        />
      )}

      {showViewGoalModal && (
        <ViewGoal
          open={showViewGoalModal}
          onClose={handleViewModalClose}
          goalId={getValueOrBlank(goalIdRef?.current)}
          isViewOnly={isViewOnly}
          onEditClick={handleEditGoal}
        />
      )}

      {showDeleteGoalConfirmation && (
        <WarningConfirmationModal
          show={showDeleteGoalConfirmation}
          onClose={handleDeleteGoalConfirmationModalClose}
          onPrimaryButtonClick={handleGoalDelete}
          onSecondaryButtonClick={handleDeleteGoalConfirmationModalClose}
          secondaryButtonTitle={commonLabels.no}
          labelContent={getLabel(deleteGoalConfirmationMessage)}
        />
      )}
    </GoalSelectionWrapper>
  );
};
